import * as THREE from "three";
import { mergeGeometries } from "three/addons/utils/BufferGeometryUtils.js";
import { projectRoundedBoxUvs, roundedBox } from "./materials";

export interface RoadBatchResult {
  root: THREE.Group;
  sourceSegments: number;
  meshes: number;
}

function disposeGeometries(geometries: readonly THREE.BufferGeometry[]): void {
  for (const geometry of geometries) geometry.dispose();
}

export async function buildRoadBatches(
  roads: Float32Array,
  sidewalkMaterial: THREE.Material,
  roadMaterial: THREE.Material,
  checkpoint: () => Promise<void>,
  signal: AbortSignal,
  maxSegments = 256,
): Promise<RoadBatchResult> {
  if (!Number.isInteger(maxSegments) || maxSegments <= 0)
    throw new RangeError("Road batch segment limit must be a positive integer");
  const root = new THREE.Group();
  root.name = "Road batches";
  let sidewalkSources: THREE.BufferGeometry[] = [];
  let roadSources: THREE.BufferGeometry[] = [];
  let sourceSegments = 0;
  let batchIndex = 0;

  const flush = async (): Promise<void> => {
    if (sidewalkSources.length === 0) return;
    signal.throwIfAborted();
    const sidewalks = mergeGeometries(sidewalkSources, false);
    disposeGeometries(sidewalkSources);
    sidewalkSources = [];
    if (!sidewalks) throw new Error("Failed to merge sidewalk geometry batch");
    const sidewalkMesh = new THREE.Mesh(sidewalks, sidewalkMaterial);
    sidewalkMesh.name = `Sidewalk batch ${batchIndex + 1}`;
    sidewalkMesh.receiveShadow = true;
    root.add(sidewalkMesh);
    await checkpoint();
    signal.throwIfAborted();
    const streets = mergeGeometries(roadSources, false);
    disposeGeometries(roadSources);
    roadSources = [];
    if (!streets) throw new Error("Failed to merge road geometry batch");
    const roadMesh = new THREE.Mesh(streets, roadMaterial);
    roadMesh.name = `Road batch ${batchIndex + 1}`;
    roadMesh.receiveShadow = true;
    root.add(roadMesh);
    batchIndex += 1;
    await checkpoint();
  };

  try {
    for (let offset = 0; offset < roads.length; offset += 5) {
      signal.throwIfAborted();
      const fromX = roads[offset] ?? 0;
      const fromZ = roads[offset + 1] ?? 0;
      const toX = roads[offset + 2] ?? 0;
      const toZ = roads[offset + 3] ?? 0;
      const width = roads[offset + 4] ?? 10;
      const deltaX = toX - fromX;
      const deltaZ = toZ - fromZ;
      const length = Math.hypot(deltaX, deltaZ);
      if (length < 0.5) continue;
      const centerX = (fromX + toX) / 2;
      const centerZ = (fromZ + toZ) / 2;
      const rotation = -Math.atan2(deltaZ, deltaX);
      const sidewalk = roundedBox(length, 0.1, width + 4.8, 0.05);
      projectRoundedBoxUvs(sidewalk);
      sidewalk.rotateY(rotation);
      sidewalk.translate(centerX, 0.04, centerZ);
      sidewalkSources.push(sidewalk);
      const road = roundedBox(length, 0.08, width, 0.04);
      projectRoundedBoxUvs(road);
      road.rotateY(rotation);
      road.translate(centerX, 0.11, centerZ);
      roadSources.push(road);
      sourceSegments += 1;
      await checkpoint();
      if (sidewalkSources.length >= maxSegments) await flush();
    }
    await flush();
    signal.throwIfAborted();
    return { root, sourceSegments, meshes: root.children.length };
  } catch (error) {
    disposeGeometries(sidewalkSources);
    disposeGeometries(roadSources);
    const disposed = new Set<THREE.BufferGeometry>();
    root.traverse((object) => {
      if (!(object instanceof THREE.Mesh) || disposed.has(object.geometry)) return;
      disposed.add(object.geometry);
      object.geometry.dispose();
    });
    root.clear();
    throw error;
  }
}
